import { diffDays, todayISO, type Account } from '@keel/core';
import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, ExternalLink, FileUp, History, RefreshCw, Unplug } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { guideHref } from '@/routes/transactions/import/guides';
import { errorCode, errorMessage, useTRPC, useTRPCClient } from '@/api/query';
import { useRefresh } from '@/components/money';
import { promptAddProperty } from '@/components/money/AccountDialog';
import {
  Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Dialog, DialogContent, Field, MoneyText, Select, Skeleton, Textarea, toast,
} from '@/components/ui';
import { useDates } from '@/lib/dates';
import { useAppStore } from '@/lib/store';
import { BRAND } from '@/brand';

interface FeedConnection {
  readonly id: string; readonly entityId?: string; readonly provider?: string; readonly status: string; readonly institutionIds: readonly string[];
  readonly lastSyncAt: string | null; readonly consentExpiresAt: string | null; readonly lastError?: string | null;
}
interface FeedAccount {
  readonly id: string; readonly name: string; readonly maskedNumber: string | null; readonly type: string; readonly balanceCents: number;
  readonly currency: string; readonly linkedAccountId: string | null; readonly suggestedAccountId?: string | null;
}
type FeedSyncResult = { readonly imported?: number; readonly duplicates?: number };

const STATUS: Record<string, { label: string; tone: 'positive' | 'warning' | 'negative' | 'neutral' }> = {
  active: { label: 'Connected', tone: 'positive' }, pending_consent: { label: 'Waiting for your bank', tone: 'warning' },
  expired: { label: 'Consent expired', tone: 'negative' }, error: { label: 'Needs attention', tone: 'negative' },
};
/** "Imported 12 new transactions (3 already in Mintage)." from whatever counts the sync returns. */
export const syncSummary = (r: FeedSyncResult): string => {
  const imported = typeof r.imported === 'number' ? r.imported : 0;
  const dup = typeof r.duplicates === 'number' && r.duplicates > 0 ? ` (${r.duplicates} already in ${BRAND.name})` : '';
  return imported === 0 ? `Up to date — no new transactions${dup}.` : `Imported ${imported} new ${imported === 1 ? 'transaction' : 'transactions'}${dup}. They're marked to review.`;
};

/**
 * App-open refresh: once per entity per page load, ask the server to sync feeds that are due (it skips connections
 * synced in the last 30 minutes). Uses the raw client so a lapsed plan or viewer role never pops a dialog.
 */
export const FeedAutoSync = ({ entityId }: { readonly entityId: string | undefined }) => {
  const client = useTRPCClient();
  const qc = useQueryClient();
  const status = useQuery(useTRPC().feeds.status.queryOptions(entityId ? { entityId } : skipToken, { meta: { silent: true }, retry: false, staleTime: Infinity }));
  const linked = (status.data?.linkedAccounts ?? 0) > 0;
  useEffect(() => {
    if (!entityId || !linked) return;
    client.feeds.syncNow.mutate({ entityId, auto: true })
      .then((r) => { if (r.imported > 0) void qc.invalidateQueries(); })
      .catch(() => undefined); // best effort: the connection row records why
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, linked]);
  return null;
};

/** Fixed wording per server reason code — never text from the URL. */
const CONNECT_FAILED: Record<string, string> = {
  cancelled: 'You cancelled before sharing any accounts. Try “Connect a bank” again when you’re ready.',
  expired: 'The connection took too long or your session changed. Try “Connect a bank” again.',
  provider: 'openfeed couldn’t complete the connection. Try again in a few minutes.',
};

interface Owner { readonly id: string; readonly type: string }
/** Whose accounts a new connection brings in: the current entity, unless it's an SMSF and a personal one exists. */
export const feedOwnerDefault = (entities: readonly Owner[], currentId: string): string => {
  const cur = entities.find((e) => e.id === currentId);
  if (!cur || cur.type !== 'smsf') return currentId;
  return (entities.find((e) => e.type === 'individual' || e.type === 'household') ?? entities.find((e) => e.type !== 'smsf') ?? cur).id;
};

/**
 * Accounts → "Bank connections": each feed connection (status, link, sync, full history, disconnect) and each account
 * kept up to date by statement import. Adding is Add account's job (AddAccountChooser). Hidden when there's nothing
 * to list, but always mounted: it handles the openfeed / own-feed return (`?feed=connected&connection=…`) review screen.
 */
export const BankFeedsCard = ({ entityId, accounts }: { readonly entityId: string; readonly accounts: readonly Account[] }) => {
  const feeds = useTRPC().feeds;
  const setEntity = useAppStore((s) => s.setEntity);
  /** After linking into another entity, show that entity (its connection and accounts live there). */
  const showOwner = (id: string) => { if (id !== entityId) setEntity(id); };
  const qc = useQueryClient();
  const dates = useDates();
  const refresh = useRefresh();
  const institutions = useQuery(feeds.institutions.queryOptions({}, { meta: { silent: true }, staleTime: 3_600_000 }));
  const nameOf = (c: FeedConnection): string => c.provider === 'simplefin' ? `Your own feed (${c.institutionIds.join(', ') || 'SimpleFIN'})` :
    c.provider === 'openfeed' ? c.institutionIds.join(', ') || 'Your bank (openfeed)' :
    c.institutionIds.map((id) => institutions.data?.find((i) => i.id === id)?.name).filter(Boolean).join(', ') || 'Your bank';
  const status = useQuery(feeds.status.queryOptions({ entityId }, { meta: { silent: true }, retry: false }));
  const list = useQuery(feeds.connections.queryOptions(status.data ? { entityId } : skipToken, { meta: { silent: true }, refetchOnWindowFocus: true }));
  const institutionsReady = institutions.data !== undefined || institutions.isError;
  const [linking, setLinking] = useState<FeedConnection | null>(null);
  const [removing, setRemoving] = useState<FeedConnection | null>(null);
  const invalidate = () => void qc.invalidateQueries({ queryKey: feeds.pathKey() });

  const sync = useMutation(feeds.syncNow.mutationOptions({
    meta: { silent: true },
    onSuccess: (r) => { toast.success(syncSummary(r)); invalidate(); void refresh(); },
    onError: (e) => toast.error(errorMessage(e)),
  }));
  const disconnect = useMutation(feeds.disconnect.mutationOptions({
    meta: { silent: true },
    onSuccess: (r) => {
      toast.success(`Bank disconnected. Transactions already imported stay in ${BRAND.name}.`, removing?.provider === 'openfeed' && !r.revoked
        ? { description: `To stop sharing completely, also turn ${BRAND.name} off under Sharing in your openfeed dashboard.` } : undefined);
      setRemoving(null); invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  }));

  // the bank sends people back to /accounts?feed=connected after consent; "Connect your own feed" lands here too (&provider=simplefin)
  const [params, setParams] = useSearchParams();
  const returned = params.get('feed');
  useEffect(() => {
    if (!returned) return;
    const connection = params.get('connection');
    const provider = params.get('provider') === 'simplefin' ? 'simplefin' : 'openfeed';
    if (returned === 'connected' && provider === 'openfeed') toast.success('Bank connected', { description: 'Choose which accounts to bring in.' });
    if (returned === 'connected' && connection) setLinking({ id: connection, provider, status: 'active', institutionIds: [], lastSyncAt: null, consentExpiresAt: null });
    if (returned === 'failed') toast.error('Bank not connected', { description: Object.hasOwn(CONNECT_FAILED, params.get('reason') ?? '') ? CONNECT_FAILED[params.get('reason')!] : CONNECT_FAILED.provider });
    invalidate();
    for (const k of ['feed', 'connection', 'message', 'provider']) params.delete(k);
    setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returned]);

  // an older API without bank feeds has no connections
  const connections = status.isError && errorCode(status.error) === 'NOT_FOUND' ? [] : list.data ?? [];
  const statementFed = accounts.filter((a) => a.importSource && !a.isClosed);

  return (
    <>
      {connections.length || statementFed.length ? (
        <Card data-testid="bank-feeds" className="mb-6">
          <CardHeader>
            <div>
              <CardTitle>Bank connections</CardTitle>
              <CardDescription>Read-only: {BRAND.name} can never move money. To add a bank or card, choose Add account.</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border rounded-lg border border-border">
              {connections.map((c) => {
                const expiresIn = c.consentExpiresAt ? diffDays(todayISO(), c.consentExpiresAt.slice(0, 10)) : null;
                return (
                  <li key={c.id} className="flex flex-wrap items-center gap-3 p-3">
                    <Building2 className="size-5 text-text-muted" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-center gap-2 font-medium text-text">
                        {institutionsReady ? nameOf(c) : 'Bank'} <Badge tone={STATUS[c.status]?.tone ?? 'neutral'}>{STATUS[c.status]?.label ?? c.status}</Badge>
                      </p>
                      <p className="text-xs text-text-muted">
                        {c.lastError ? <span className="text-negative">{c.lastError} · </span> : null}
                        {c.lastSyncAt ? `Last synced ${dates.format(c.lastSyncAt.slice(0, 10))}` : 'Not synced yet'}
                        {c.consentExpiresAt ? (
                          <span className={expiresIn !== null && expiresIn <= 14 ? 'font-medium text-warning' : undefined}>
                            {' · '}{expiresIn !== null && expiresIn < 0 ? 'Consent expired — reconnect' : `Consent until ${dates.format(c.consentExpiresAt.slice(0, 10))}`}
                          </span>
                        ) : null}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => setLinking(c)}>Link accounts</Button>
                      <Button size="sm" variant="outline" icon={<RefreshCw aria-hidden />} loading={sync.isPending && sync.variables?.connectionId === c.id}
                        onClick={() => sync.mutate({ entityId, connectionId: c.id })}>Sync now</Button>
                      {c.lastSyncAt ? (
                        <Button size="sm" variant="ghost" icon={<History aria-hidden />} loading={sync.isPending && sync.variables?.connectionId === c.id && !!sync.variables?.history}
                          title="Fetch every transaction the bank shares (usually 2 years or more). Already-imported ones are skipped and balances still match the bank."
                          onClick={() => sync.mutate({ entityId, connectionId: c.id, history: true })}>Get full history</Button>
                      ) : null}
                      <Button size="sm" variant="ghost" icon={<Unplug aria-hidden />} onClick={() => setRemoving(c)}>Disconnect</Button>
                    </div>
                  </li>
                );
              })}
              {statementFed.map((a) => (
                <li key={a.id} className="flex flex-wrap items-center gap-3 p-3" data-testid="statement-account">
                  <FileUp className="size-5 text-text-muted" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-text">{a.name}</p>
                    <p className="text-xs text-text-muted">Updated by statement import</p>
                  </div>
                  <Button asChild size="sm" variant="outline"><Link to={guideHref(a.importSource === 'amex_csv' ? 'amex' : 'bank', a.id)}>Import latest<span className="sr-only"> for {a.name}</span></Link></Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
      {linking ? (
        <LinkDialog entityId={linking.entityId ?? entityId} connection={linking} name={nameOf(linking)} {...((linking.entityId ?? entityId) === entityId ? { accounts } : {})}
          onClose={() => { setLinking(null); showOwner(linking.entityId ?? entityId); }} onLinked={(id) => sync.mutate({ entityId: linking.entityId ?? entityId, connectionId: id })} />
      ) : null}
      <Dialog open={removing !== null} onOpenChange={(o) => !o && setRemoving(null)}>
        <DialogContent
          title={`Disconnect ${removing ? nameOf(removing) : 'this bank'}?`}
          description={`${BRAND.name} stops fetching new transactions and withdraws its access at the bank. Everything already imported stays, and you can reconnect any time.`}
          footer={
            <>
              <Button variant="ghost" onClick={() => setRemoving(null)}>Keep it connected</Button>
              <Button variant="danger" loading={disconnect.isPending} onClick={() => removing && disconnect.mutate({ entityId, connectionId: removing.id })}>Disconnect</Button>
            </>
          }
        />
      </Dialog>
    </>
  );
};

/** "Connect your own feed": paste a SimpleFIN setup token from Redbark (AU/NZ) or SimpleFIN Bridge (US). */
export const OwnFeedDialog = ({ entityId, onClose, onConnected }: {
  readonly entityId: string; readonly onClose: () => void; readonly onConnected: (connectionId: string) => void;
}) => {
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | undefined>();
  const connect = useMutation(useTRPC().feeds.connectSimplefin.mutationOptions({
    meta: { silent: true },
    onSuccess: ({ connectionId }) => { toast.success('Feed connected', { description: 'Choose which accounts to bring in.' }); onConnected(connectionId); },
    onError: (e) => setError(errorMessage(e)),
  }));
  const submit = () => {
    if (token.trim().length < 10) return setError('Paste the setup token from your feed provider.');
    setError(undefined);
    connect.mutate({ entityId, setupToken: token.trim() });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title="Connect your own feed"
        description={`Use a feed you subscribe to yourself. ${BRAND.name} only reads transactions and balances, and you can revoke access any time.`}
        footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button loading={connect.isPending} onClick={submit}>Connect</Button></>}>
        <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); submit(); }}>
          <ul className="list-disc space-y-1 pl-5 text-sm text-text-muted">
            <li><strong className="text-text">Australia (incl. CommBank):</strong> Redbark, Developer plan — Settings → SimpleFIN → generate a setup token.</li>
            <li><strong className="text-text">United States:</strong> SimpleFIN Bridge — create a setup token on your Bridge dashboard.</li>
            <li>You pay the provider directly; {BRAND.name} charges nothing extra. Revoke the app at the provider, or disconnect here, at any time.</li>
          </ul>
          <Field label="Setup token" error={error} hint="A long code. It works once — if it fails, generate a new one.">
            <Textarea value={token} onChange={(e) => setToken(e.target.value)} rows={3} autoComplete="off" spellCheck={false} className="font-mono text-xs" />
          </Field>
        </form>
        <p className="mt-3 text-xs text-text-muted">
          <a className="underline" href="/docs/importing/bank-feeds" target="_blank" rel="noreferrer">Step-by-step guide<ExternalLink className="ml-1 inline size-3" aria-hidden /></a>
        </p>
      </DialogContent>
    </Dialog>
  );
};

const SKIP = '__skip';
const CREATE = '__create';

/** Map each bank account to a Mintage account (or create one); unchanged rows are left alone. `accounts` defaults to the entity's. */
const LinkDialog = ({ entityId, connection, name, accounts: given, onClose, onLinked }: {
  readonly entityId: string; readonly connection: FeedConnection; readonly name: string; readonly accounts?: readonly Account[]; readonly onClose: () => void;
  readonly onLinked: (connectionId: string) => void;
}) => {
  const trpc = useTRPC();
  const feeds = trpc.feeds;
  const own = useQuery(trpc.accounts.list.queryOptions(given ? skipToken : { entityId }));
  const accounts = given ?? own.data ?? [];
  const qc = useQueryClient();
  const refresh = useRefresh();
  const q = useQuery(feeds.accounts.queryOptions({ entityId, connectionId: connection.id }, { meta: { silent: true } }));
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const link = useMutation(feeds.link.mutationOptions({ meta: { silent: true } }));
  const navigate = useNavigate();
  // Pre-filled: keep an existing link, else the suggested matching account, else create one. "Don't bring in" is one click away.
  const current = (a: FeedAccount): string => choice[a.id] ?? a.linkedAccountId ?? a.suggestedAccountId ?? CREATE;
  const options = [
    { value: SKIP, label: 'Don’t bring in' },
    { value: CREATE, label: `Create a new ${BRAND.name} account` },
    ...accounts.filter((a) => !a.isClosed).map((a) => ({ value: a.id, label: a.name })),
  ];
  const save = async () => {
    const changed = (q.data ?? []).filter((a) => current(a) !== SKIP && current(a) !== a.linkedAccountId);
    setSaving(true);
    try {
      const mortgages: { id: string; name: string }[] = [];
      for (const a of changed) {
        const v = current(a);
        const r = await link.mutateAsync({ entityId, connectionId: connection.id, feedAccountId: a.id, ...(v === CREATE ? { createAccount: true } : { accountId: v }) });
        const linked = v === CREATE ? r.links.find((l) => l.feedAccountId === a.id)?.accountId : v;
        const existing = accounts.find((x) => x.id === linked);
        if (linked && (existing ? existing.type === 'mortgage' && !existing.securedByAccountId : a.type === 'mortgage')) mortgages.push({ id: linked, name: existing?.name ?? a.name });
      }
      if (changed.length) {
        toast.success(`Linked ${changed.length} ${changed.length === 1 ? 'account' : 'accounts'}`, { description: 'Importing transactions now. They arrive marked “To review”.' });
        if (mortgages[0]) promptAddProperty(mortgages[0], navigate);
        onLinked(connection.id);
      } else toast.success('Nothing changed.');
      void qc.invalidateQueries({ queryKey: feeds.pathKey() });
      void refresh();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg" title={`Link ${name} accounts`}
        description="We’ve suggested where each account goes: an existing matching account, or a new one. Change any, or choose “Don’t bring in”. Linking to an existing account skips duplicates."
        footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button loading={saving} disabled={!q.data} onClick={() => void save()}>Save links</Button></>}>
        {q.isPending ? (
          <div role="status" className="flex flex-col gap-3">
            <p className="text-sm text-text-muted">Fetching your accounts from the bank… this can take up to 30 seconds.</p>
            <Skeleton className="h-16" /><Skeleton className="h-16" /><Skeleton className="h-16" />
          </div>
        ) : q.isError ? <p className="text-sm text-text">{errorMessage(q.error)}</p> : (
          <ul className="flex flex-col gap-3">
            {(q.data ?? []).map((a) => (
              <li key={a.id} className="grid items-center gap-2 rounded-lg border border-border p-3 sm:grid-cols-[minmax(0,1fr)_16rem]">
                <div className="min-w-0">
                  <p className="truncate font-medium text-text">{a.name}</p>
                  <p className="text-xs text-text-muted">
                    {[a.maskedNumber, a.type].filter(Boolean).join(' · ')}
                    {typeof a.balanceCents === 'number' ? <> · <MoneyText cents={a.balanceCents} currency={a.currency} /></> : null}
                  </p>
                </div>
                <Select aria-label={`${BRAND.name} account for ${a.name}`} value={current(a)} onValueChange={(v) => setChoice({ ...choice, [a.id]: v })} options={options} />
              </li>
            ))}
            {q.data?.length === 0 ? <li className="text-sm text-text-muted">The bank hasn’t shared any accounts on this connection yet. Try again in a minute.</li> : null}
          </ul>
        )}
        <p className="mt-3 flex items-center gap-1 text-xs text-text-muted"><ExternalLink className="size-3.5" aria-hidden />Imported rows arrive marked “To review” in Transactions.</p>
      </DialogContent>
    </Dialog>
  );
};
